import React, { useRef, useEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { AgeGrade } from "../../../types/api";
import api from "../../../api";

gsap.registerPlugin(ScrollTrigger);

const AgeGradeList = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [ageGrades, setAgeGrades] = useState<AgeGrade[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAgeGrades = async () => {
      try {
        const response = await api.get("/age-grades");
        setAgeGrades(response.data);
      } catch (error) {
        console.error("Failed to fetch age grades:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAgeGrades();
  }, []);

  useEffect(() => {
    if (loading) return;

    const section = sectionRef.current;
    if (!section) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) return;

    const cards = gsap.utils.toArray(".age-grade-card");
    gsap.from(cards, {
      opacity: 0,
      y: 50,
      duration: 0.8,
      ease: "power3.out",
      stagger: 0.15,
      scrollTrigger: {
        trigger: section,
        start: "top 80%",
        toggleActions: "play none none none",
      },
    });

    cards.forEach((card) => {
      const cardEl = card as HTMLElement;
      const borderEl = cardEl.querySelector(".card-border");

      const hoverTl = gsap
        .timeline({ paused: true })
        .to(cardEl, { y: -8, duration: 0.3, ease: "power2.out" })
        .to(
          borderEl,
          { width: "100%", duration: 0.4, ease: "power2.inOut" },
          0
        );

      cardEl.addEventListener("mouseenter", () => hoverTl.play());
      cardEl.addEventListener("mouseleave", () => hoverTl.reverse());
    });

    return () => {
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, [loading]);

  if (loading) {
    return (
      <section className="bg-stone-50 py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-6 text-center">
          <h2 className="mb-12 font-serif text-3xl font-semibold text-nature-green-dark md:text-4xl">
            Our Community Structure
          </h2>
          <div className="text-lg text-gray-600">Loading age grades...</div>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} className="bg-stone-50 py-20 md:py-28">
      <div className="mx-auto max-w-6xl px-6">
        <h2 className="mb-12 text-center font-serif text-3xl font-semibold text-nature-green-dark md:text-4xl">
          Our Community Structure
        </h2>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {ageGrades.map((grade) => (
            <div
              key={grade.id}
              className="age-grade-card relative transform-gpu overflow-hidden rounded-2xl border border-stone-200/70 bg-white p-8 shadow-sm transition-transform"
            >
              <div className="card-border absolute left-0 top-0 h-1.5 w-0 bg-amber-400"></div>
              <h3 className="text-2xl font-bold text-gray-800">{grade.name}</h3>
              <p className="mt-1 font-semibold text-amber-600">
                Ages: {grade.min_age} – {grade.max_age}
              </p>
              <p className="mt-4 h-24 text-gray-600">
                {grade.description ||
                  "Core duties include community development, event organization, and upholding cultural values."}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AgeGradeList;
